import React, { type ReactNode } from "react";
import FusionTheme from "@directv-digital/fusion.themes.fusion";
import MUITextField, { TextFieldProps } from "@mui/material/TextField";
import  HelperMessage  from "@directv-digital/fusion.atoms.helper-message";
import { ErrorIcon, MagnifyingGlassIcon, ShowPasswordIcon, HidePasswordIcon } from "@directv-digital/fusion.atoms.icons";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

export type Mode = "dark" | "light";
export type TextFieldVariant = "empty" | "filled" | "placeholder";
export type IconVariant = "leftIcon" | "rightIcon" | "clearIcon" | "all";

export interface DTVTextFieldProps extends Omit<TextFieldProps, "variant"> {
  /**
   * sets the component children.
   */
  children?: ReactNode;
  /**
   * sets color mode of text-field either dark/light
   */
  mode?: Mode;
  /**
   * sets the placeholder of the textfield
   */
  placeHolder?: string;
  /**
   * text-field label text, shows on top of text-field when active
   */
  label?: string;
  /**
   * default value of the textfield at render.
   */
  defaultValue?: string;
  /**
   * disables all pointer actions on the text-fiend
   */
  disabled?: boolean;
  /**
   * Tells form control if the field is required.
   */
  required?: boolean;
  /**
   * If there was an error in the text-field
   */
  error?: boolean;
  /**
   * Boolean value for if the helpermessage should be visable.
   */
  helperMessage?: boolean;
  /**
   * Helper message text. Only shows if helperMessageVissable is set to true.
   */
  helperMessageText?: string;
  /**
   * Boolean for if the helper message icon should be visable. Only shows if helperMessageVissable is set to true.
   */
  helperMessageIcon?: boolean;
  /**
   * Sets the icon variant of text-field. "leftIcon": for only left icon | "rightIcon": for only right icon | "clearIcon": for no icons | "all": for all icons
   */
  iconVariant?: IconVariant;
  /**
   * width of the text-field. If not provided it will be set automatically depending on parent.
   */
  width?: string;
  /**
   * If the textfield has a value
   */
  value?: any;
  /**
   * The type of the TextField
   */
  type?: string;
  /**
   * Custom left icon
   */
  leftIcon?: ReactNode;
  /**
   * Custom right icon
   */
  rightIcon?: ReactNode;
  /**
   * onChange function for when text is added to the text-field
   */
  onChange?: (e?: any) => void;
  /**
   * Functionality for right icon
   */
  rightIconClick?: (e?: any) => void;
}

export function TextField({
  children,
  mode,
  placeHolder,
  label,
  defaultValue,
  disabled,
  required,
  error,
  helperMessage,
  helperMessageText,
  helperMessageIcon,
  iconVariant,
  width,
  value,
  type,
  leftIcon,
  rightIcon,
  onChange,
  rightIconClick,
  sx,
  ...otherProps
}: DTVTextFieldProps) {
  const [inputValue, setInputValue] = React.useState(defaultValue || "");
  const [focused, setFocused] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showPassword, setShowPassword] = React.useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClick = e => {
    // (rightIconClick && rightIconClick(e)) || type === "password"
    //   ? setShowPassword(!showPassword)
    //   : setInputValue("");
  };

  const handleChange = e => {
    onChange && onChange(e);
    setInputValue(e?.target.value);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFocus = e => {
    setFocused(true);
  };

  const handleBlur = e => {
    setFocused(false);
    otherProps?.onBlur(e);
  };

  return (
    <ThemeProvider theme={FusionTheme}>
      <CssBaseline />
      <MUITextField
        {...otherProps}
        disabled={disabled}
        error={error}
        id="outlined-required"
        label={label ? label : "Label"}
        placeholder={placeHolder && placeHolder}
        required={required}
        type={type === "password" ? (showPassword ? "text" : "password") : type}
        value={inputValue}
        variant="outlined"
        InputLabelProps={{
          ...otherProps?.InputLabelProps,
          sx: {
            ...FusionTheme.typography["Body 1"],
            top: {
 sm: "-2px",
md: "-2px",
lg: "-2px"
},
            "&.MuiInputLabel-shrink": {
              top: "2px",
              left: "2px",
              ...FusionTheme.typography["Body 3"]
            },

            "& legend": {
              marginLeft: "200px"
            },
            ...otherProps?.InputLabelProps?.sx
          },
          shrink: focused || inputValue !== "",
          style: {
            color: disabled ?
              FusionTheme.fusion.palette["field-inactive"] :
              mode === "dark" ?
              FusionTheme.fusion.palette["field-inverse-label"] :
              FusionTheme.fusion.palette["field-label"],
            marginLeft:
              !(focused || inputValue !== "") &&
              (iconVariant === "leftIcon" || iconVariant === "all") ?
                "34px" :
                0
          }
        }}
        InputProps={{
          ...otherProps?.InputProps,
          startAdornment: (iconVariant === "leftIcon" ||
            iconVariant === "all") && (
            <InputAdornment position="start">
              {leftIcon || (
                <MagnifyingGlassIcon
                  fill={FusionTheme.fusion.palette["field-icon"]}
                />
              )}
            </InputAdornment>
          ),
          endAdornment: (iconVariant === "rightIcon" ||
            iconVariant === "all") &&
            inputValue && (
              <InputAdornment position="end">
                <IconButton
                  disableFocusRipple
                  aria-label="clear field"
                  disabled={disabled}
                  sx={{
                    padding: FusionTheme.fusion.space.spacing04,
                    marginRight: FusionTheme.fusion.space.spacing04,
                    "&.Mui-focusVisible": {
                      outline: `4px solid ${FusionTheme.fusion.palette["accessibility-focusRing"]}`,
                      outlineOffset: "2px"
                    }
                  }}
                  onClick={handleClick}
                >
                  {rightIcon ||
                    (type === "password" ? (
                      showPassword ? (
                        <HidePasswordIcon
                          fill={
                            disabled ?
                              FusionTheme.fusion.palette[
                                  "field-icon-inactive"
                                ] :
                              mode === "dark" ?
                              FusionTheme.fusion.palette[
                                  "field-inverse-icon-control"
                                ] :
                              FusionTheme.fusion.palette["field-icon-control"]
                          }
                        />
                      ) : (
                        <ShowPasswordIcon
                          fill={
                            disabled ?
                              FusionTheme.fusion.palette[
                                  "field-icon-inactive"
                                ] :
                              mode === "dark" ?
                              FusionTheme.fusion.palette[
                                  "field-inverse-icon-control"
                                ] :
                              FusionTheme.fusion.palette["field-icon-control"]
                          }
                        />
                      )
                    ) : (
                      <ErrorIcon
                        fill={
                          disabled ?
                            FusionTheme.fusion.palette["field-icon-inactive"] :
                            mode === "dark" ?
                            FusionTheme.fusion.palette[
                                "field-inverse-icon-control"
                              ] :
                            FusionTheme.fusion.palette["field-icon-control"]
                        }
                      />
                    ))}
                </IconButton>
              </InputAdornment>
            )
        }}
        helperText={
          helperMessage && (
            <HelperMessage
              error={error}
              iconVisible={helperMessageIcon && !disabled}
              message={helperMessageText}
              mode={mode}
            />
          )
        }
        sx={{
          ...sx,
          pointerEvents: disabled ? "none" : "",
          width: width ? width : {
 sm: "335px",
md: "335px",
lg: "270px"
},
          minWidth: width ? width : {
 sm: "335px",
md: "335px",
lg: "270px"
},
          "& .MuiInputBase-root": {
            Height: {
 sm: "48px",
md: "48px",
lg: "56px"
},
            maxHeight: {
 sm: "48px",
md: "48px",
lg: "56px"
},
            paddingLeft: FusionTheme.fusion.space.spacing05,
            paddingRight: "0px",
            "& fieldset legend": {
              marginLeft: "2px"
            }
          },
          "& .MuiInputLabel-shrink": {
            color:
              error &&
              (mode === "dark" ?
                `${FusionTheme.fusion.palette["field-inverse-label-error"]} !important` :
                `${FusionTheme.fusion.palette["field-label-error"]} !important`)
          },
          "& .MuiInputBase-input": {
            ...FusionTheme.typography["Body 1"],
            paddingLeft: 0,
            color:
              mode === "dark" ?
                FusionTheme.fusion.palette["field-inverse-input"] :
                FusionTheme.fusion.palette["field-input"],
            "&::placeholder": {
              color:
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-placeholder"] :
                  FusionTheme.fusion.palette["field-placeholder"],
              opacity: 1
            },
            "&.Mui-disabled": {
              WebkitTextFillColor: FusionTheme.fusion.palette["field-inactive"]
            }
          },
          "& .MuiFormHelperText-root": {
            padding: 0,
            margin: 0
          },
          "& .MuiInputLabel-root": {},
          "& svg": {
            height: "24px",
            width: "24px"
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: disabled ?
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"] :
                mode === "dark" ?
                FusionTheme.fusion.palette["field-inverse-border-active"] :
                FusionTheme.fusion.palette["field-border-active"]
            },
            "&.Mui-disabled fieldset": {
              borderColor:
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"]
            },
            "&:hover fieldset": {
              borderColor: disabled ?
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"] :
                mode === "dark" ?
                FusionTheme.fusion.palette["field-inverse-border-hover"] :
                FusionTheme.fusion.palette["field-border-hover"]
            },
            "&.Mui-focused fieldset": {
              borderColor: disabled ?
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"] :
                mode === "dark" ?
                FusionTheme.fusion.palette["field-inverse-border-engaged"] :
                FusionTheme.fusion.palette["field-border-engaged"]
            }
          },
          "& .Mui-error": {
            "& fieldset": {
              borderColor: disabled ?
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"] :
                FusionTheme.fusion.palette["field-border-error"]
            },
            "&:hover fieldset": {
              borderColor: disabled ?
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"] :
                mode === "dark" ?
                FusionTheme.fusion.palette["field-inverse-border-hover-error"] :
                FusionTheme.fusion.palette["field-border-hover-error"]
            },
            "&.Mui-focused fieldset": {
              borderColor: disabled ?
                mode === "dark" ?
                  FusionTheme.fusion.palette["field-inverse-border-inactive"] :
                  FusionTheme.fusion.palette["field-border-inactive"] :
                mode === "dark" ?
                FusionTheme.fusion.palette[
                    "field-inverse-border-engaged-error"
                  ] :
                FusionTheme.fusion.palette["field-border-engaged-error"]
            }
          },
          ...FusionTheme.typography["Body 1"]
        }}
        onBlur={e => handleBlur(e)}
        onChange={e => handleChange(e)}
        onFocus={e => handleFocus(e)}
      />
    </ThemeProvider>
  );
}
